import { Skeleton } from '@/components/ui/skeleton';
import { useCurvaTeorica } from '@/hooks/useLaboratorio';
import type { Ensayo } from '@adelante/shared';
import { useMemo } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

/**
 * Gráfico de resistencia vs edad para una muestra.
 *
 * - Línea continua: curva teórica esperada (ASTM C-150), basada en
 *   `lab.curva_teorica` × F'C objetivo de la muestra.
 * - Puntos: ensayos reales (promedio kg/cm² a cada edad probada).
 * - Línea horizontal: F'C objetivo como referencia.
 *
 * Eje X: edad en días (escala lineal, 0-90 para los puntos típicos;
 * la curva teórica llega hasta 365 pero la mostramos solo hasta el
 * máximo donde hay datos o 90, lo que sea mayor).
 * Eje Y: resistencia en kg/cm².
 *
 * Si no hay puntos reales todavía, mostramos solo la curva teórica
 * (sirve para que el operador vea las "metas" antes de fallar cilindros).
 */

const FACTOR_MPA_A_KGCM2 = 10.197;
const COLOR_TEORICA = '#94A3B8'; // gris azulado
const COLOR_REAL = '#ADD010'; // verde Adelante
const COLOR_FC_REF = '#DC2626'; // rojo (línea de referencia F'C)

interface Props {
  ensayos: Ensayo[];
  fcObjetivo: number;
}

interface PuntoSerie {
  edad_dias: number;
  teorica: number;
  real?: number;
  /** Curva teórica escalada a los datos reales (proyección). */
  proyectada?: number;
}

export function GraficoResistenciaMuestra({ ensayos, fcObjetivo }: Props) {
  const curva = useCurvaTeorica();
  const puntosCurva = curva.data?.data?.puntos ?? [];

  // Puntos reales: 1 por ensayo con promedio kg/cm² (si tiene mediciones).
  const puntosReales = useMemo(() => {
    return ensayos
      .filter((e) => e.resistencia_kg_cm2_promedio !== null)
      .map((e) => ({
        edad_dias: e.edad_dias,
        kg_cm2: e.resistencia_kg_cm2_promedio as number,
      }));
  }, [ensayos]);

  // Determinamos el rango del eje X. Mostramos curva teórica hasta el max
  // de (90 días, ensayo más viejo). Más allá de 90 días los puntos teóricos
  // tienen menos variación y agregan poco visualmente.
  const maxEdadEnsayos = puntosReales.reduce((m, p) => Math.max(m, p.edad_dias), 0);
  const xMax = Math.max(90, maxEdadEnsayos + 5);

  // Factor de proyección: escala la curva teórica para que pase lo mejor
  // posible por los puntos reales (mínimos cuadrados a través del origen).
  // Sirve para proyectar la resistencia a edades futuras (ej. 28d) a partir
  // de los ensayos tempranos ya rotos. null si todavía no hay datos reales.
  const factorProyeccion = useMemo(() => {
    if (puntosReales.length === 0) return null;
    let num = 0;
    let den = 0;
    for (const p of puntosReales) {
      const teoricaKg = interpolarCurva(puntosCurva, p.edad_dias, fcObjetivo);
      if (teoricaKg > 0) {
        num += p.kg_cm2 * teoricaKg;
        den += teoricaKg * teoricaKg;
      }
    }
    return den > 0 ? num / den : null;
  }, [puntosReales, puntosCurva, fcObjetivo]);

  // Construir serie combinada para recharts: una fila por edad de la curva,
  // limitando a edad <= xMax. Si una edad coincide con un ensayo real, se
  // agrega el campo `real` para que Scatter lo dibuje.
  const serie = useMemo(() => {
    const realesPorEdad = new Map(puntosReales.map((p) => [p.edad_dias, p.kg_cm2]));
    const datos: PuntoSerie[] = [];

    // 1) Puntos de la curva teórica
    for (const p of puntosCurva) {
      if (p.edad_dias > xMax) continue;
      datos.push({
        edad_dias: p.edad_dias,
        teorica: Math.round(p.pct_resistencia * fcObjetivo * 10) / 10,
        real: realesPorEdad.get(p.edad_dias),
      });
      realesPorEdad.delete(p.edad_dias);
    }
    // 2) Puntos reales que NO tenían coincidencia con la curva (ej. ensayo
    //    a los 21 días) — los agregamos como puntos sueltos con `teorica`
    //    interpolada linealmente entre los puntos de curva más cercanos.
    for (const [edad, kgcm2] of realesPorEdad.entries()) {
      datos.push({
        edad_dias: edad,
        teorica: interpolarCurva(puntosCurva, edad, fcObjetivo),
        real: kgcm2,
      });
    }
    // Proyección: escalamos la teórica de cada fila por el factor ajustado.
    if (factorProyeccion !== null) {
      for (const d of datos) {
        d.proyectada = Math.round(d.teorica * factorProyeccion * 10) / 10;
      }
    }
    datos.sort((a, b) => a.edad_dias - b.edad_dias);
    return datos;
  }, [puntosCurva, puntosReales, xMax, fcObjetivo, factorProyeccion]);

  if (curva.isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  if (puntosCurva.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        Curva teórica no disponible (revisar tabla lab.curva_teorica).
      </div>
    );
  }

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={serie} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            type="number"
            dataKey="edad_dias"
            domain={[0, xMax]}
            ticks={[1, 3, 7, 14, 28, 56, 90].filter((t) => t <= xMax)}
            label={{ value: 'Edad (días)', position: 'insideBottom', offset: -2, fontSize: 11 }}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            label={{
              value: 'kg/cm²',
              angle: -90,
              position: 'insideLeft',
              fontSize: 11,
              offset: 10,
            }}
          />
          <Tooltip content={<TooltipPersonalizado fcObjetivo={fcObjetivo} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} verticalAlign="top" height={24} />

          {/* Línea de referencia F'C objetivo */}
          <ReferenceLine
            y={fcObjetivo}
            stroke={COLOR_FC_REF}
            strokeDasharray="5 5"
            strokeWidth={1}
            label={{
              value: `F'C = ${fcObjetivo}`,
              position: 'right',
              fontSize: 10,
              fill: COLOR_FC_REF,
            }}
          />

          {/* Curva teórica */}
          <Line
            type="monotone"
            dataKey="teorica"
            stroke={COLOR_TEORICA}
            strokeWidth={2}
            dot={false}
            name="Teórica (ASTM C-150)"
          />

          {/* Proyección: curva teórica escalada a los datos reales */}
          {factorProyeccion !== null && (
            <Line
              type="monotone"
              dataKey="proyectada"
              stroke={COLOR_REAL}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              name="Proyección (según datos)"
            />
          )}

          {/* Puntos reales (Scatter sobre la misma data) */}
          <Scatter dataKey="real" fill={COLOR_REAL} name="Real" shape="circle" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Interpola linealmente entre los dos puntos de curva más cercanos a `edad`.
 * Útil para mostrar la teórica de una edad no estándar (ej. 21 días).
 */
function interpolarCurva(
  puntos: { edad_dias: number; pct_resistencia: number }[],
  edad: number,
  fc: number,
): number {
  if (puntos.length === 0) return 0;
  // Si la edad coincide con un punto, devolvemos directo.
  const exacto = puntos.find((p) => p.edad_dias === edad);
  if (exacto) return Math.round(exacto.pct_resistencia * fc * 10) / 10;
  // Buscar inmediatos anterior y posterior.
  const anteriores = puntos.filter((p) => p.edad_dias < edad);
  const posteriores = puntos.filter((p) => p.edad_dias > edad);
  if (anteriores.length === 0)
    return Math.round((posteriores[0]?.pct_resistencia ?? 0) * fc * 10) / 10;
  if (posteriores.length === 0)
    return Math.round((anteriores.at(-1)?.pct_resistencia ?? 0) * fc * 10) / 10;
  const a = anteriores.at(-1) as (typeof anteriores)[0];
  const b = posteriores[0] as (typeof posteriores)[0];
  // Interpolación lineal
  const t = (edad - a.edad_dias) / (b.edad_dias - a.edad_dias);
  const pct = a.pct_resistencia + t * (b.pct_resistencia - a.pct_resistencia);
  return Math.round(pct * fc * 10) / 10;
}

function TooltipPersonalizado({
  active,
  payload,
  fcObjetivo,
}: TooltipProps<number, string> & { fcObjetivo: number }) {
  if (!active || !payload || payload.length === 0) return null;
  const data = (payload[0] as unknown as { payload: PuntoSerie }).payload;
  if (!data) return null;

  const pctReal = data.real !== undefined ? (data.real / fcObjetivo) * 100 : null;
  const pctTeorica = (data.teorica / fcObjetivo) * 100;

  return (
    <div className="rounded-md border bg-background p-2 text-xs shadow-md">
      <div className="font-semibold">{data.edad_dias} días</div>
      <div className="mt-1 flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: COLOR_TEORICA }}
        />
        <span>Teórica:</span>
        <span className="font-mono">{data.teorica.toFixed(0)} kg/cm²</span>
        <span className="text-muted-foreground">({pctTeorica.toFixed(0)}%)</span>
      </div>
      {data.real !== undefined && (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: COLOR_REAL }}
          />
          <span>Real:</span>
          <span className="font-mono font-medium">{data.real.toFixed(0)} kg/cm²</span>
          {pctReal !== null && (
            <span className="text-muted-foreground">({pctReal.toFixed(0)}%)</span>
          )}
        </div>
      )}
      {data.proyectada !== undefined && data.real === undefined && (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block h-0 w-3 border-t-2 border-dashed"
            style={{ borderColor: COLOR_REAL }}
          />
          <span>Proyección:</span>
          <span className="font-mono">{data.proyectada.toFixed(0)} kg/cm²</span>
          <span className="text-muted-foreground">
            ({((data.proyectada / fcObjetivo) * 100).toFixed(0)}%)
          </span>
        </div>
      )}
      {data.real !== undefined && (
        <div className="mt-1 border-t pt-1 text-muted-foreground">
          Δ vs teórica: {((data.real / data.teorica - 1) * 100).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

// Suprimimos el "unused" cuando MPa-helper queda no usado aún; lo dejamos
// disponible por si alguna versión futura del tooltip lo necesita.
void FACTOR_MPA_A_KGCM2;
